import { Fragment } from 'react';
import { parseBlocks, type Span } from './markdown-lite';

function Spans({ spans }: { spans: Span[] }) {
  return (
    <>
      {spans.map((s, i) => (
        <Fragment key={i}>
          {s.bold ? (
            <strong className="font-medium text-foreground">{s.text}</strong>
          ) : s.code ? (
            <code className="rounded bg-muted px-1 font-mono text-[10px]">{s.text}</code>
          ) : (
            s.text
          )}
        </Fragment>
      ))}
    </>
  );
}

/**
 * The briefing as separate cards rather than one list.
 *
 * The briefing returns a handful of independent findings — a person away while
 * their task is due, a sprint that ended with work still open. Stacked as bullets
 * in one box they read as a paragraph to skim; as cards they read as a queue of
 * things to deal with, which is what they are.
 *
 * Deliberately uniform: the model is asked for most-urgent-first, but nothing in
 * the text reliably says how bad each line is, and inventing severity from
 * keywords would put a red border on a guess.
 */
export function AttentionCards({ text }: { text: string }) {
  const blocks = parseBlocks(text);
  const bullets = blocks.filter((b) => b.type === 'bullet' || b.type === 'numbered');

  // A briefing with nothing to report is one plain sentence — one card, no index.
  const items = bullets.length > 0 ? bullets : blocks.slice(0, 1);
  if (items.length === 0) return null;

  return (
    <div className="grid gap-2 sm:grid-cols-2 2xl:grid-cols-3">
      {items.map((item, i) => (
        <div
          key={i}
          className="group relative overflow-hidden rounded-xl border border-border bg-card/40 p-4 transition-colors hover:border-hygge/40"
        >
          <span
            aria-hidden
            className="absolute inset-y-0 left-0 w-[3px] bg-hygge/40 transition-colors group-hover:bg-hygge"
          />
          <div className="flex gap-3">
            {bullets.length > 0 && (
              <span className="mt-px shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">
                {String(i + 1).padStart(2, '0')}
              </span>
            )}
            <p className="min-w-0 flex-1 text-[13px] leading-relaxed text-muted-foreground">
              <Spans spans={item.spans} />
            </p>
          </div>
        </div>
      ))}
    </div>
  );
}
